// Typed client for the talk-tracks REST API (/v1/studio/talk-tracks/*).
// See app/api/studio_talk_tracks_router.py.

import { ApiError, apiRequest } from "./http";
import type {
  BenchTurnResult,
  ContentCheck,
  ConversationTimeline,
  CreateTopicInput,
  ImportResult,
  LibraryTopicStats,
  Simulation,
  StatsFilters,
  TalkTrackComment,
  TalkTrackContent,
  TalkTrackMeta,
  TalkTrackTopic,
  TalkTrackVersion,
  TopicDetail,
  TopicListItem,
  TopicPatch,
  TopicStats,
  UnitCall,
  UnitSuggestions,
  VersionDetail,
} from "../types/talkTrack";

const enc = encodeURIComponent;
const BASE = "/studio/talk-tracks";

const topicPath = (topicId: string) => `${BASE}/${enc(topicId)}`;
const versionPath = (topicId: string, version: number) =>
  `${topicPath(topicId)}/versions/${version}`;

// Message for any thrown value (422s carry {message, checks}; http.ts already
// flattens that into ApiError.message).
export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// Blocking checks attached to a 422 from submit/approve/publish, if any.
export function errorChecks(e: unknown): ContentCheck[] | null {
  if (!(e instanceof ApiError) || !e.detail || typeof e.detail !== "object") return null;
  const checks = (e.detail as { checks?: unknown }).checks;
  return Array.isArray(checks) ? (checks as ContentCheck[]) : null;
}

export const getMeta = () => apiRequest<TalkTrackMeta>(`${BASE}/meta`);

// ---- topics

export const listTopics = () => apiRequest<TopicListItem[]>(BASE);

export const createTopic = (input: CreateTopicInput) =>
  apiRequest<TopicDetail>(BASE, { method: "POST", body: input });

export const importBundle = (topics: unknown[], updateExisting = false) =>
  apiRequest<ImportResult>(`${BASE}/import`, { method: "POST", body: { topics, updateExisting } });

export const getTopic = (topicId: string) => apiRequest<TopicDetail>(topicPath(topicId));

export const updateTopic = (topicId: string, patch: TopicPatch) =>
  apiRequest<TalkTrackTopic>(topicPath(topicId), { method: "PATCH", body: patch });

// ---- performance

// Never-published topics only; published ones can only be disabled.
export const deleteTopic = (topicId: string) =>
  apiRequest<void>(topicPath(topicId), { method: "DELETE" });

// Query string for the stats endpoints; filters left at their default are omitted.
function statsQuery(days: number, filters?: Partial<StatsFilters>): string {
  const q = new URLSearchParams({ days: String(days) });
  if (filters?.inputMode) q.set("inputMode", filters.inputMode);
  if (filters?.excludeTopUser) q.set("excludeTopUser", "true");
  return q.toString();
}

export const getLibraryStats = (days = 30, filters?: Partial<StatsFilters>) =>
  apiRequest<{ days: number; lowSampleUsers?: number; topics: LibraryTopicStats[] }>(
    `${BASE}/stats?${statsQuery(days, filters)}`,
  );

export const getTopicStats = (topicId: string, days = 30, filters?: Partial<StatsFilters>) =>
  apiRequest<TopicStats>(`${topicPath(topicId)}/stats?${statsQuery(days, filters)}`);

// Same filters as the topic stats, so the drill-down matches the unit row.
export const getUnitCalls = (topicId: string, unitId: string, days = 30, filters?: Partial<StatsFilters>) =>
  apiRequest<{ unitId: string; calls: UnitCall[] }>(
    `${topicPath(topicId)}/units/${enc(unitId)}/calls?${statsQuery(days, filters)}`,
  );

// Every move of the call's conversation (its sibling connections included).
export const getCallTimeline = (callId: string) =>
  apiRequest<ConversationTimeline>(`${BASE}/calls/${enc(callId)}/timeline`);

// ---- versions + lifecycle

export const ensureDraft = (topicId: string, fromVersion?: number) =>
  apiRequest<TalkTrackVersion>(`${topicPath(topicId)}/drafts`, {
    method: "POST",
    body: { fromVersion: fromVersion ?? null },
  });

export const getVersion = (topicId: string, version: number) =>
  apiRequest<VersionDetail>(versionPath(topicId, version));

export const saveDraft = (topicId: string, version: number, content: TalkTrackContent) =>
  apiRequest<VersionDetail>(versionPath(topicId, version), { method: "PUT", body: { content } });

// Drafts only; the server refuses a topic's only version.
export const discardDraft = (topicId: string, version: number) =>
  apiRequest<void>(versionPath(topicId, version), { method: "DELETE" });

export const submitVersion = (topicId: string, version: number) =>
  apiRequest<TalkTrackVersion>(`${versionPath(topicId, version)}/submit`, { method: "POST" });

export const approveVersion = (topicId: string, version: number, note: string) =>
  apiRequest<TalkTrackVersion>(`${versionPath(topicId, version)}/approve`, {
    method: "POST",
    body: { note: note || null },
  });

export const requestChanges = (topicId: string, version: number, note: string) =>
  apiRequest<TalkTrackVersion>(`${versionPath(topicId, version)}/request-changes`, {
    method: "POST",
    body: { note: note || null },
  });

export const publishVersion = (topicId: string, version: number, rolloutPct: number) =>
  apiRequest<TalkTrackTopic>(`${versionPath(topicId, version)}/publish`, {
    method: "POST",
    body: { rolloutPct },
  });

export const restoreVersion = (topicId: string, version: number) =>
  apiRequest<TalkTrackTopic>(`${versionPath(topicId, version)}/restore`, { method: "POST" });

export const setRollout = (topicId: string, pct: number) =>
  apiRequest<TalkTrackTopic>(`${topicPath(topicId)}/rollout`, { method: "POST", body: { pct } });

export const promoteCandidate = (topicId: string) =>
  apiRequest<TalkTrackTopic>(`${topicPath(topicId)}/promote`, { method: "POST" });

export const rollbackCandidate = (topicId: string) =>
  apiRequest<TalkTrackTopic>(`${topicPath(topicId)}/rollback`, { method: "POST" });

// ---- comments

export const listComments = (topicId: string, version: number) =>
  apiRequest<TalkTrackComment[]>(`${versionPath(topicId, version)}/comments`);

export const addComment = (topicId: string, version: number, body: string, unitId?: string) =>
  apiRequest<TalkTrackComment>(`${versionPath(topicId, version)}/comments`, {
    method: "POST",
    body: { body, unitId: unitId ?? null },
  });

export const resolveComment = (commentId: string, resolved: boolean) =>
  apiRequest<TalkTrackComment>(`${BASE}/comments/${enc(commentId)}/resolve`, {
    method: "POST",
    body: { resolved },
  });

// ---- bench, simulations, copilot

export const benchTurn = (params: {
  guideId: string;
  topicId: string;
  version: number;
  message: string;
  sessionId: string | null;
}) => apiRequest<BenchTurnResult>(`${BASE}/bench/turn`, { method: "POST", body: params });

export const startSimulation = (topicId: string, version: number, guideId: string) =>
  apiRequest<Simulation>(`${versionPath(topicId, version)}/simulations`, {
    method: "POST",
    body: { guideId },
  });

export const listSimulations = (topicId: string, version?: number) =>
  apiRequest<{ simulations: Simulation[] }>(
    `${topicPath(topicId)}/simulations${version !== undefined ? `?version=${version}` : ""}`,
  );

export const getSimulation = (simId: string) =>
  apiRequest<Simulation>(`${BASE}/simulations/${enc(simId)}`);

export const suggestRewrite = (topicId: string, version: number, unitId: string) =>
  apiRequest<UnitSuggestions>(`${versionPath(topicId, version)}/units/${enc(unitId)}/suggest`, {
    method: "POST",
  });
